import { type ReactElement } from 'react'
import { useRequiredParams } from '@util'
import { type TeamId, type TeamLevel, type TinyTeam } from '@terros/common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { getTeamFullName, useProfile, useTeam, useTeams, useTranslation } from '@front/model'
import {
  EditTeamButton,
  NotReady,
  Page,
  TAlert,
  TeamTitle,
  TeamViewContent,
  useTeamViewTab,
  type BreadCrumbItem,
} from '@components'

type Props = {
  teamId: TeamId
}

function Team(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { teamId } = useRequiredParams<Props>()
  const { company } = useProfile()
  const parents = useTeamParentTree(teamId)
  const [activeTab, setActiveTab] = useTeamViewTab()

  const { data, isLoading, error, refetch, isFetching } = useTeam(teamId)

  if (isLoading || error || !data) return <NotReady data={data} error={error} id={t('team')} isLoading={isLoading} />
  const { team } = data

  const teamFullname = getTeamFullName(company, team)

  const breadcrumbs: BreadCrumbItem[] = [
    {
      label: t('Teams'),
      href: '/team',
    },
  ]

  parents.forEach((t, index) => {
    const level: TeamLevel = (team.level + parents.length - index) as TeamLevel
    breadcrumbs.push({
      label: getTeamFullName(company, { name: t.name, level }),
      href: `/team/${t.teamId}`,
    })
  })

  breadcrumbs.push({
    label: teamFullname,
    href: `#`,
    disabled: true,
  })

  return (
    <Page>
      <Page.Heading
        back
        breadcrumbs={breadcrumbs}
        isFetching={isFetching}
        refetch={refetch}
        title={<TeamTitle size='md' team={team} />}
        metadata={{
          title: teamFullname,
        }}
      >
        <EditTeamButton teamId={teamId} />
      </Page.Heading>
      {team.isDeleted && <TAlert variant='basic'>{t('This team is in Trash')}</TAlert>}
      <Page.Content scrollArea>
        <TeamViewContent
          activeTab={activeTab}
          teamId={teamId}
          onNavigateTeam={(nextTeamId) => navigate({ to: `/team/${nextTeamId}` })}
          onTabChange={setActiveTab}
        />
      </Page.Content>
    </Page>
  )
}

const useTeamParentTree = (teamId: TeamId): TinyTeam[] => {
  const { data } = useTeams()
  const { user } = useProfile()
  if (!data || !user) return []

  const tinyTeams = data
  const team = tinyTeams.find((t) => t.teamId === teamId)
  if (!team) return []

  const parents: TinyTeam[] = []
  const addParent = (teamData: TinyTeam): void => {
    if (teamData.parentId) {
      const parent = tinyTeams.find((t) => t.teamId === teamData.parentId)
      if (parent) {
        addParent(parent)
        parents.push(parent)
      }
    }
  }
  addParent(team)

  return parents
}

export const Route = createFileRoute('/team_/$teamId/')({
  component: Team,
})
